<script>
	import { onMount, onDestroy } from "svelte";
	import { currentUser } from "../store/store.js";
	import { navigate } from "svelte-routing";
	import { User } from "../utils/login.js";
	import { writable } from "svelte/store";
	import { Circle2 } from "svelte-loading-spinners";
	import {
		validateOrganizationNumber,
		formatOrganizationNumber,
	} from "../utils/profile.js";
	import ProfileHeader from "../components/profile/ProfileHeader.svelte";
	import SubscriptionBanner from "../components/profile/SubscriptionBanner.svelte";
	import ProfileDetails from "../components/profile/ProfileDetails.svelte";
	import SubscriptionUpgrade from "../components/profile/SubscriptionUpgrade.svelte";
	import ProfileFavourites from "../components/profile/ProfileFavourites.svelte";
	import "../components/profile/profile.css";

	let email = "";
	let firstName = "";
	let lastName = "";
	let organizationNumber = "";
	let organizationNumberInput = "";
	let organizationNumberError = "";

	let loading = writable(false);
	let generalError = "";
	let actionLoading = false;
	let isSyncOnCooldown = false;
	let cooldownTimer = null;
	let syncButtonHiddenAfterSuccess = false;

	const stripeBasePlusPaymentLink =
		"https://buy.stripe.com/3cIdR92Wf557gYF1jKdIA01";
	const stripeBaseCancelLink =
		"https://billing.stripe.com/p/login/bJe9AT68r413dMtfaAdIA00";
	let stripePlusPaymentLink = "";
	let stripeCancelLink = "";

	$: {
		if ($currentUser) {
			email = $currentUser.username || "";
			firstName = $currentUser.first_name || "";
			lastName = $currentUser.last_name || "";
			organizationNumber = $currentUser.organisation_number || "";
			if (email) {
				const encodedEmail = encodeURIComponent(email);
				stripePlusPaymentLink = `${stripeBasePlusPaymentLink}?prefilled_email=${encodedEmail}`;
				stripeCancelLink = `${stripeBaseCancelLink}?prefilled_email=${encodedEmail}`;
			} else {
				stripePlusPaymentLink = stripeBasePlusPaymentLink;
				stripeCancelLink = stripeBaseCancelLink;
			}
		} else {
			email = "";
			firstName = "";
			lastName = "";
			organizationNumber = "";
			organizationNumberInput = "";
			organizationNumberError = "";
			stripePlusPaymentLink = stripeBasePlusPaymentLink;
			stripeCancelLink = stripeBaseCancelLink;
		}
	}

	$: isPremium =
		$currentUser &&
		($currentUser.subscription_status === "active" ||
			$currentUser.subscription_status === "trialing" ||
			$currentUser.subscription_tier === "Plus" ||
			$currentUser.subscription_tier === "Pro");

	onMount(async () => {
		syncButtonHiddenAfterSuccess = false;
		loading.set(true);
		const isAuthenticated = await checkAuthStatus();
		if (!isAuthenticated && (!$currentUser || !$currentUser.accessToken)) {
			navigate("/");
		}
		loading.set(false);
	});

	async function checkAuthStatus() {
		try {
			const storedUser = localStorage.getItem("currentUser");
			if (storedUser) {
				try {
					const userData = JSON.parse(storedUser);
					currentUser.set(new User(userData));
					return true;
				} catch (e) {
					console.error("Error parsing stored user data:", e);
					localStorage.removeItem("currentUser");
				}
			}
			if ($currentUser && $currentUser.accessToken) return true;

			try {
				const response = await fetch(
					`https://boanalys-backend-324523837899.europe-north1.run.app/users/me`,
					{
						credentials: "include",
						headers: { Accept: "application/json" },
					},
				);

				if (response.ok) {
					const serverData = await response.json();
					const currentTokenInStore =
						$currentUser?.accessToken ||
						JSON.parse(localStorage.getItem("currentUser"))
							?.accessToken;
					const userObj = new User({
						accessToken: currentTokenInStore,
						username: serverData.username,
						type: serverData.type,
						numberOfAnalysis: serverData.number_of_analysis,
						first_name: serverData.first_name,
						last_name: serverData.last_name,
						stripe_customer_id: serverData.stripe_customer_id,
						subscription_current_period_end:
							serverData.subscription_current_period_end,
						subscription_status: serverData.subscription_status,
						subscription_tier: serverData.subscription_tier,
						organisation_number: serverData.organisation_number,
						favourites: serverData.favourites || [],
					});
					localStorage.setItem(
						"currentUser",
						JSON.stringify(userObj),
					);
					currentUser.set(userObj);
					return true;
				} else if (response.status === 401) {
					currentUser.set(null);
					localStorage.removeItem("currentUser");
					return false;
				} else {
					console.error(
						`Authentication check failed: ${response.status} ${response.statusText}`,
					);
					return false;
				}
			} catch (error) {
				console.error("Error checking authentication status:", error);
				return false;
			}
		} catch (error) {
			console.error("Error checking authentication status:", error);
			return false;
		}
	}

	async function syncSubscriptionStatus() {
		if (!$currentUser?.accessToken) {
			generalError = "Autentisering krävs. Logga in igen.";
			return;
		}
		if (isSyncOnCooldown) {
			generalError = "Vänta en stund innan du försöker uppdatera igen.";
			return;
		}
		actionLoading = true;
		isSyncOnCooldown = true;
		try {
			const headers = { "Content-Type": "application/json" };
			if ($currentUser?.accessToken)
				headers["Authorization"] = `Bearer ${$currentUser.accessToken}`;
			const response = await fetch(
				`https://boanalys-backend-324523837899.europe-north1.run.app/users/me/sync-subscription`,
				{
					method: "GET",
					credentials: "include",
					headers,
				},
			);

			if (response.ok) {
				const responseText = await response.text();
				try {
					const parsedData = JSON.parse(responseText);
					if (
						typeof parsedData === "object" &&
						parsedData !== null &&
						!Array.isArray(parsedData)
					) {
						const serverData = parsedData;
						const currentTokenInStore = $currentUser?.accessToken;
						if (!currentTokenInStore) {
							generalError =
								"Kunde inte uppdatera användarinformationen lokalt, token saknas.";
							await checkAuthStatus();
							actionLoading = false;
							return;
						}
						const userObj = new User({
							accessToken: currentTokenInStore,
							username: serverData.username,
							type: serverData.type,
							numberOfAnalysis: serverData.number_of_analysis,
							first_name: serverData.first_name,
							last_name: serverData.last_name,
							stripe_customer_id: serverData.stripe_customer_id,
							subscription_current_period_end:
								serverData.subscription_current_period_end,
							subscription_status: serverData.subscription_status,
							subscription_tier: serverData.subscription_tier,
							organisation_number: serverData.organisation_number,
							favourites: serverData.favourites || [],
						});
						localStorage.setItem(
							"currentUser",
							JSON.stringify(userObj),
						);
						currentUser.set(userObj);
						generalError = "";
					} else if (typeof parsedData === "string") {
						generalError =
							parsedData.trim() !== ""
								? parsedData
								: "Synkroniseringen slutfördes med ett tomt meddelande.";
						await checkAuthStatus();
					} else {
						generalError =
							"Mottog oväntat dataformat från servern efter lyckad synkronisering.";
						await checkAuthStatus();
					}
				} catch (e) {
					generalError = responseText?.trim()
						? responseText
						: "Mottog ett tomt eller ogiltigt textmeddelande från servern.";
					await checkAuthStatus();
				}
				if (
					$currentUser &&
					($currentUser.subscription_status === "active" ||
						$currentUser.subscription_status === "trialing" ||
						$currentUser.subscription_tier === "Plus" ||
						$currentUser.subscription_tier === "Pro")
				) {
					syncButtonHiddenAfterSuccess = true;
				} else {
					syncButtonHiddenAfterSuccess = false;
				}
			} else {
				let errorMessage =
					"Ett fel uppstod vid synkronisering av prenumerationsstatus.";
				try {
					const errorResponseContent = await response.json();
					if (
						typeof errorResponseContent === "string" &&
						errorResponseContent.trim() !== ""
					) {
						errorMessage = errorResponseContent;
					} else if (errorResponseContent?.detail) {
						errorMessage = errorResponseContent.detail;
					}
				} catch (e) {}
				generalError = errorMessage;
				await checkAuthStatus();
				syncButtonHiddenAfterSuccess = false;
			}
		} catch (error) {
			console.error("Error syncing subscription status:", error);
			generalError =
				"Ett nätverksfel uppstod vid synkronisering. Försök igen.";
			await checkAuthStatus();
			syncButtonHiddenAfterSuccess = false;
		} finally {
			actionLoading = false;
			cooldownTimer = setTimeout(() => {
				isSyncOnCooldown = false;
			}, 30000);
		}
	}

	function validateInputRealTime() {
		if (!organizationNumberInput) {
			organizationNumberError = "";
			return;
		}
		const formatted = formatOrganizationNumber(organizationNumberInput);
		const validation = validateOrganizationNumber(formatted);
		organizationNumberError = validation.valid ? "" : validation.error;
	}

	function handleFormatBlur() {
		if (organizationNumberInput) {
			organizationNumberInput = formatOrganizationNumber(
				organizationNumberInput,
			);
			validateInputRealTime();
		}
	}

	async function updateOrganizationNumber() {
		if (!$currentUser?.accessToken) {
			generalError =
				"Du är inte inloggad. Vänligen logga in igen för att fortsätta.";
			return;
		}
		const formatted = formatOrganizationNumber(organizationNumberInput);
		const validation = validateOrganizationNumber(formatted);
		if (organizationNumberInput && !validation.valid) {
			generalError = validation.error;
			return;
		}
		actionLoading = true;
		generalError = "";
		try {
			const url = new URL(
				"https://boanalys-backend-324523837899.europe-north1.run.app/users/me/set_organisation_number",
			);
			url.searchParams.append("organisation_number", formatted);
			const response = await fetch(url.toString(), {
				method: "POST",
				credentials: "include",
				headers: { Accept: "application/json" },
			});
			if (response.ok) {
				const updatedUser = new User({
					...$currentUser,
					organisation_number: formatted,
				});
				localStorage.setItem(
					"currentUser",
					JSON.stringify(updatedUser),
				);
				currentUser.set(updatedUser);
				organizationNumberInput = "";
				generalError =
					"✅ Organisationsnummer sparat! Du är nu kopplad till bostadsrättsföreningen.";
			} else {
				let errorMessage = "Ett okänt fel uppstod vid sparandet.";
				if (response.status === 401)
					errorMessage =
						"Din session har gått ut. Vänligen logga in igen.";
				else if (response.status === 403)
					errorMessage =
						"Du saknar behörighet att uppdatera organisationsnummer.";
				else if (response.status === 422)
					errorMessage =
						"Organisationsnumret är inte giltigt eller saknas.";
				else if (response.status === 500)
					errorMessage =
						"Ett serverfel uppstod. Försök igen om en stund.";
				else {
					try {
						const errorData = await response.json();
						if (errorData.detail) errorMessage = errorData.detail;
					} catch (e) {}
				}
				generalError = errorMessage;
			}
		} catch (error) {
			console.error("Error updating organization number:", error);
			if (error.name === "TypeError" && error.message.includes("fetch")) {
				generalError =
					"Ingen internetanslutning. Kontrollera din uppkoppling och försök igen.";
			} else if (error.name === "AbortError") {
				generalError = "Begäran avbröts. Försök igen.";
			} else {
				generalError =
					"Ett nätverksfel uppstod. Kontrollera din anslutning och försök igen.";
			}
		} finally {
			actionLoading = false;
		}
	}

	onDestroy(() => {
		if (cooldownTimer) clearTimeout(cooldownTimer);
	});
</script>

<div class="profile-page">
	<div class="page-container"></div>

	{#if $loading && !email && !firstName && !lastName}
		<div
			style="display: flex; justify-content: center; align-items: center; min-height: 50vh;"
		>
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
	{:else if generalError && !$currentUser && !actionLoading}
		<div
			class="content-box"
			style="margin-top: 10vh; max-width: 800px; text-align: center;"
		>
			<p class="error-message">{generalError}</p>
		</div>
	{:else if $currentUser?.accessToken}
		<ProfileHeader {firstName} />

		<div class="profile-content">
			<div class="profile-main-column">
				{#if generalError && !actionLoading}
					<div class="content-box error-banner">
						<p
							class={generalError.startsWith("✅")
								? "success-message"
								: "error-message"}
						>
							{generalError}
						</p>
					</div>
				{/if}

				{#if isPremium}
					<SubscriptionBanner
						subscriptionStatus={$currentUser.subscription_status}
						subscriptionCurrentPeriodEnd={$currentUser.subscription_current_period_end}
						{stripeCancelLink}
						{actionLoading}
						{isSyncOnCooldown}
						{syncButtonHiddenAfterSuccess}
						onSync={syncSubscriptionStatus}
					/>
				{/if}

				<ProfileDetails
					{email}
					{firstName}
					{lastName}
					subscriptionStatus={$currentUser.subscription_status}
					{organizationNumber}
					bind:organizationNumberInput
					{organizationNumberError}
					{actionLoading}
					{isSyncOnCooldown}
					{isPremium}
					onEditOrgNumber={() => {
						organizationNumberInput = organizationNumber;
					}}
					onUpdateOrgNumber={updateOrganizationNumber}
					onCancelOrgEdit={() => {
						organizationNumberInput = "";
						organizationNumberError = "";
					}}
					onValidateInput={validateInputRealTime}
					onFormatBlur={handleFormatBlur}
					onSyncSubscription={syncSubscriptionStatus}
				/>

				{#if !isPremium}
					<SubscriptionUpgrade {stripePlusPaymentLink} />
				{/if}
			</div>

			<div class="profile-side-column">
				<ProfileFavourites
					favourites={$currentUser?.favourites || []}
				/>
			</div>

		</div>
	{:else}
		<p style="text-align: center; margin-top: 20px;">Omdirigerar...</p>
	{/if}
</div>

<style>
	@import "../components/profile/profile.css";
</style>
